import { Link } from "react-router-dom";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import ScrollToTop from "../../components/feature/ScrollToTop";
import SEOHead from "../../components/base/SEOHead";
import { features4K, supportedLeagues4K } from "../../mocks/sports4K";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://xn--9z2b23f95curk71m6qd.com";

const sports4KJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "4K 스포츠중계",
  description: "UHD 초고화질로 생생한 스포츠중계를 경험하세요. 3840x2160 해상도와 HDR 컬러로 실제 경기장 같은 몰입감을 선사합니다.",
  about: {
    "@type": "Service",
    name: "4K 스포츠중계",
    provider: {
      "@type": "Organization",
      name: "할매티비",
      url: siteUrl,
    },
    description: "UHD 4K 초고화질 스포츠 실시간 중계 서비스",
  },
};

export default function Sports4K() {
  return (
    <div className="min-h-screen bg-dark-bg">
      <SEOHead
        title="4K 스포츠중계 | UHD 초고화질 실시간 중계 - 할매티비"
        description="4K 스포츠중계로 UHD 초고화질 실시간 경기를 감상하세요. 3840x2160 해상도, HDR 컬러, 다중 카메라 앵글로 프리미어리그, 라리가, 챔피언스리그 등 주요 리그를 최고 화질로 시청하세요."
        keywords="4K스포츠중계,UHD중계,초고화질중계,4K해상도,HDR중계,프리미어리그4K"
        canonicalPath="/4k-sports"
        jsonLd={sports4KJsonLd}
      />
      <Header />
      <main className="pt-24 md:pt-32 pb-16">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-white font-bold text-2xl md:text-3xl mb-2">
              4K 스포츠중계
            </h1>
            <p className="text-dark-text-muted text-base">
              UHD 초고화질로 생생한 스포츠중계를 경험하세요.
            </p>
          </div>

          {/* Hero Banner */}
          <div className="bg-dark-surface border border-dark-border rounded-xl p-6 md:p-8 mb-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-brand-orange/10 rounded-full border border-brand-orange/30">
              <i className="ri-hd-line text-brand-orange text-3xl"></i>
            </div>
            <h2 className="text-white font-bold text-xl md:text-2xl mb-2">
              UHD 4K 초고화질 스트리밍
            </h2>
            <p className="text-dark-text-muted text-sm max-w-lg mx-auto mb-6">
              4K 스포츠중계는 3840x2160 해상도와 HDR 컬러로 실제 경기장 같은 몰입감을 선사합니다.
            </p>
            <Link
              to="/sports-schedule"
              className="group inline-flex items-center gap-3 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-base py-4 px-10 rounded-full transition-all whitespace-nowrap"
            >
              <i className="ri-movie-line text-xl"></i>
              <span>4K 중계 시청하기</span>
              <i className="ri-arrow-right-up-line text-lg opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"></i>
            </Link>
          </div>

          {/* 4K Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
            {features4K.map((feature, i) => (
              <div
                key={i}
                className="bg-dark-surface border border-dark-border rounded-xl p-5 hover:border-brand-orange transition-all"
              >
                <div className="w-10 h-10 flex items-center justify-center mb-3 bg-brand-orange/10 rounded-lg">
                  <i className={`${feature.icon} text-brand-orange text-xl`}></i>
                </div>
                <h3 className="text-white font-bold text-sm mb-2">{feature.title}</h3>
                <p className="text-dark-text-muted text-xs leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Supported Leagues */}
          <div className="mb-12">
            <h2 className="text-white font-bold text-xl mb-6">4K 지원 리그</h2>
            <div className="flex flex-wrap gap-3">
              {supportedLeagues4K.map((league, i) => (
                <span
                  key={i}
                  className="text-white text-sm font-medium bg-dark-surface border border-dark-border px-4 py-2 rounded-full hover:border-brand-orange transition-colors"
                >
                  {league}
                </span>
              ))}
            </div>
          </div>

          {/* Quality Comparison */}
          <div className="bg-dark-surface border border-dark-border rounded-xl p-5 md:p-6 mb-12">
            <h2 className="text-white font-bold text-lg mb-4">화질 비교</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <span className="text-dark-text-muted text-xs w-16">4K UHD</span>
                <div className="flex-1 h-4 bg-dark-bg rounded-full overflow-hidden">
                  <div className="h-full bg-brand-orange rounded-full" style={{ width: "100%" }}></div>
                </div>
                <span className="text-brand-orange font-bold text-xs">3840x2160</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-dark-text-muted text-xs w-16">Full HD</span>
                <div className="flex-1 h-4 bg-dark-bg rounded-full overflow-hidden">
                  <div className="h-full bg-dark-text-muted rounded-full" style={{ width: "50%" }}></div>
                </div>
                <span className="text-dark-text-muted font-bold text-xs">1920x1080</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-dark-text-muted text-xs w-16">HD</span>
                <div className="flex-1 h-4 bg-dark-bg rounded-full overflow-hidden">
                  <div className="h-full bg-dark-text-muted rounded-full" style={{ width: "28%" }}></div>
                </div>
                <span className="text-dark-text-muted font-bold text-xs">1280x720</span>
              </div>
            </div>
          </div>

          {/* SEO Content */}
          <div className="border-t border-dark-border pt-8">
            <h2 className="text-white font-bold text-xl mb-4">4K 스포츠중계의 차별화</h2>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">4K 스포츠중계</strong>는 기존 HD 중계와 비교할 수 없는 선명함을 제공합니다. 축구공의 회전, 야구공의 궤적, 농구공의 림을 스치는 순간까지 4K 해상도라면 놓치지 않습니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed mb-4">
              <strong className="text-white">할매티비</strong>의 <strong className="text-white">4K 스포츠중계</strong>는 HDR 기술을 적용하여 밝은 경기장과 어두운 관중석의 명암비를 자연스럽게 표현합니다. 낮 경기의 푸른 하늘과 밤 경기의 스타디움 조명 모두 생생하게 전달됩니다.
            </p>
            <p className="text-dark-text-muted text-sm leading-relaxed">
              <strong className="text-white">스포츠중계</strong>의 품질은 화질에서 결정됩니다. 4K 스포츠중계로 프리미어리그, 라리가, 챔피언스리그, MLB, NBA 등 주요 리그를 최고의 화질로 시청하세요. 다중 카메라 앵글 전환 기능으로 나만의 시점으로 경기를 감상할 수 있습니다.
            </p>
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}